
import React, { useState, useEffect } from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, List, LayoutGrid, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Appointment, AppointmentType } from '@/types/appointment';

// Mock appointments data
const mockAppointments = [
  {
    id: '1',
    patientId: '1',
    patientName: 'John Smith',
    date: new Date().toISOString(),
    time: '09:00',
    duration: 30,
    type: 'clinic',
    status: 'scheduled'
  },
  {
    id: '2',
    patientId: '2',
    patientName: 'Emily Johnson',
    date: addDays(new Date(), 1).toISOString(),
    time: '14:30',
    duration: 20,
    type: 'video',
    status: 'confirmed'
  },
  {
    id: '3',
    patientId: '3',
    patientName: 'Michael Brown',
    date: addDays(new Date(), 2).toISOString(),
    time: '11:15',
    duration: 15,
    type: 'voice',
    status: 'completed'
  }
];

const CalendarViewPage: React.FC = () => {
  const [view, setView] = useState<'list' | 'calendar'>('calendar');
  const [date, setDate] = useState(new Date());
  const { toast } = useToast();
  const navigate = useNavigate();

  // Update these to use string IDs instead of numbers
  const [pendingAppointments] = useState<Appointment[]>([
    {
      id: '101',
      patientId: '1',
      doctorId: 'doc-1',
      patientName: 'John Smith',
      appointmentDate: '2025-04-18',
      appointmentTime: '09:30',
      duration: 30,
      appointmentType: 'clinic',
      status: 'scheduled',
      date: new Date('2025-04-18T09:30:00').toISOString(),
      time: '09:30',
      type: 'clinic'
    }
  ]);
  
  const [recentAppointments] = useState<Appointment[]>([
    {
      id: '102',
      patientId: '2',
      doctorId: 'doc-1',
      patientName: 'Sarah Johnson',
      appointmentDate: '2025-04-15',
      appointmentTime: '14:00',
      duration: 45,
      appointmentType: 'clinic',
      status: 'completed',
      date: new Date('2025-04-15T14:00:00').toISOString(),
      time: '14:00',
      type: 'clinic'
    }
  ]);
  
  const [tomorrowAppointments] = useState<Appointment[]>([
    {
      id: '103',
      patientId: '3',
      doctorId: 'doc-1',
      patientName: 'Michael Brown',
      appointmentDate: '2025-04-17',
      appointmentTime: '11:00',
      duration: 30,
      appointmentType: 'video',
      status: 'scheduled',
      date: new Date('2025-04-17T11:00:00').toISOString(),
      time: '11:00',
      type: 'video'
    }
  ]);

  const handleViewDetails = (appointmentId: string) => {
    toast({
      title: "View Appointment",
      description: `Navigating to appointment ${appointmentId} details.`,
    });
    // Navigate to appointment details page
    navigate(`/doctor-area/appointments/${appointmentId}`);
  };

  const handleCreateAppointment = () => {
    toast({
      title: "Create Appointment",
      description: "Navigating to create appointment page.",
    });
    // Navigate to create appointment page
    navigate('/doctor-area/appointments/new');
  };

  return (
    <div className="container mx-auto p-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Calendar</h1>
          <p className="text-muted-foreground">
            Here&apos;s a quick overview of your appointments for the week.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="hidden sm:flex items-center space-x-1 rounded-md border p-1">
            <Button
              variant={view === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('calendar')}
            >
              <CalendarIcon className="h-4 w-4 mr-1" />
              Calendar
            </Button>
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
          </div>
          <Button onClick={handleCreateAppointment}>
            <PlusCircle className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="past">Past</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending Appointments</CardTitle>
                <CardDescription>
                  Appointments that need confirmation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-none space-y-2">
                  {pendingAppointments.map((appointment) => (
                    <li
                      key={appointment.id}
                      className="flex items-center justify-between"
                    >
                      <span>{appointment.patientName}</span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewDetails(appointment.id)}
                      >
                        View
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Recent Appointments</CardTitle>
                <CardDescription>
                  Recently completed appointments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-none space-y-2">
                  {recentAppointments.map((appointment) => (
                    <li
                      key={appointment.id}
                      className="flex items-center justify-between"
                    >
                      <span>{appointment.patientName}</span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewDetails(appointment.id)}
                      >
                        View
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Tomorrow&apos;s Appointments</CardTitle>
                <CardDescription>
                  Get ready for tomorrow&apos;s schedule
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-none space-y-2">
                  {tomorrowAppointments.map((appointment) => (
                    <li
                      key={appointment.id}
                      className="flex items-center justify-between"
                    >
                      <span>{appointment.patientName}</span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewDetails(appointment.id)}
                      >
                        View
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="upcoming" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                Here you can see all your upcoming appointments. You can
                filter, sort, and manage them as needed.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="past" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Past Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                Here you can review your past appointments. You can generate
                reports, analyze trends, and more.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CalendarViewPage;
